const TONE = {
  operational: { dot: "bg-emerald-500", text: "text-emerald-700", soft: "bg-emerald-50 border-emerald-200" },
  degraded: { dot: "bg-amber-500", text: "text-amber-700", soft: "bg-amber-50 border-amber-200" },
  outage: { dot: "bg-red-500", text: "text-red-700", soft: "bg-red-50 border-red-200" },
  unknown: { dot: "bg-slate-300", text: "text-slate-500", soft: "bg-slate-50 border-slate-200" },
};
const BLOCK = { up: "bg-emerald-500", degraded: "bg-amber-400", down: "bg-red-500", nodata: "bg-slate-200" };

export const tone = (s) => TONE[s] || TONE.unknown;
export const fmtTime = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
export const fmtDay = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
export const dur = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);

// 168 hourly blocks don't fit a phone: fold every `n` hours into one block (status = worst of the readings).
export function mergeSegments(segments, n) {
  const out = [];
  for (let i = 0; i < segments.length; i += n) {
    const group = segments.slice(i, i + n);
    const up = group.reduce((a, s) => a + s.up, 0), down = group.reduce((a, s) => a + s.down, 0);
    out.push({ t: group[0].t, up, down, status: up + down === 0 ? "nodata" : down === 0 ? "up" : up === 0 ? "down" : "degraded" });
  }
  return out;
}

export function SegmentBar({ segments, testId, blockClass, title }) {
  const bar = (segs, cls, id) => (
    <div className={`${cls} gap-[2px] h-8`} data-testid={id}>
      {segs.map((s) => <span key={s.t} className={`flex-1 rounded-[2px] ${blockClass[s.status]} hover:opacity-60 transition-opacity`} data-status={s.status} title={title(s)} />)}
    </div>
  );
  return <>{bar(segments, "hidden sm:flex", testId)}{bar(mergeSegments(segments, 4), "flex sm:hidden", `${testId}-compact`)}</>;
}

export function OverallBanner({ data }) {
  const t = tone(data.overall);
  return (
    <div className={`rounded-2xl border px-6 py-5 flex items-center gap-4 ${t.soft}`} data-testid="public-status-overall" data-status={data.overall}>
      <span className={`relative flex h-4 w-4 shrink-0`}>
        {data.overall === "operational" && <span className={`absolute inline-flex h-full w-full rounded-full ${t.dot} opacity-40 animate-ping`} />}
        <span className={`relative inline-flex h-4 w-4 rounded-full ${t.dot}`} />
      </span>
      <div className="min-w-0">
        <p className={`text-2xl sm:text-3xl font-extrabold font-[Chivo] leading-tight ${t.text}`} data-testid="public-status-overall-label">{data.overall_label}</p>
        <p className="text-xs text-slate-500 mt-1">Updated {fmtTime(data.generated_at)} · refreshes every minute</p>
      </div>
    </div>
  );
}

export function ComponentRow({ c }) {
  const t = tone(c.status);
  return (
    <div className="py-5 border-b border-slate-200 last:border-0" data-testid={`public-component-${c.key}`} data-status={c.status}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className={`h-2.5 w-2.5 rounded-full ${t.dot}`} />
          <span className="text-base font-semibold text-slate-900">{c.label}</span>
        </div>
        <span className={`text-sm font-semibold ${t.text}`} data-testid={`public-component-status-${c.key}`}>{c.status_label}</span>
      </div>
      <div className="mt-3">
        <SegmentBar segments={c.segments} testId={`public-bar-${c.key}`} blockClass={BLOCK}
                    title={(s) => `${fmtTime(s.t)} — ${s.status === "nodata" ? "no readings" : s.status === "up" ? "operational" : `${s.down} min affected`}`} />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500 tabular-nums">
        <span>{c.days ?? 7} days ago</span>
        <span data-testid={`public-uptime-${c.key}`}>{c.uptime_7d == null ? "no data yet" : `${c.uptime_7d}% uptime`}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

export function IncidentDay({ day, items }) {
  return (
    <div className="py-4 border-b border-slate-200 last:border-0" data-testid="public-incident-day">
      <p className="text-sm font-semibold text-slate-900">{fmtDay(day)}</p>
      {items.length === 0 ? <p className="text-sm text-slate-400 mt-1">No incidents reported.</p> : (
        <ul className="mt-2 space-y-2">
          {items.map((i) => (
            <li key={`${i.check}-${i.started_at}`} className="flex items-start gap-3 text-sm" data-testid="public-incident">
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${i.ongoing ? "bg-red-500 animate-pulse" : "bg-slate-400"}`} />
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{i.label} {i.ongoing ? "disruption — ongoing" : "disruption — resolved"}</p>
                <p className="text-xs text-slate-500">{fmtTime(i.started_at)}{i.ended_at ? ` → ${fmtTime(i.ended_at)}` : ""} · {dur(i.duration_minutes)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
